import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Divider,
  Stack,
  alpha,
} from '@mui/material';
import {
  LinkedIn,
  Email,
} from '@mui/icons-material';

const footerLinks = {
  product: {
    title: 'Product',
    links: [
      { label: 'Features', path: '/features' },
      { label: 'Solutions', path: '/solutions' },
      { label: 'AaaS', path: '/aaas' },
      { label: 'Pricing', path: '/pricing' },
    ],
  },
  solutions: {
    title: 'Solutions',
    links: [
      { label: 'Highway Authorities', path: '/solutions/highway-authorities' },
      { label: 'Smart Cities', path: '/solutions/municipal-councils' },
      { label: 'Toll Operators', path: '/solutions/toll-operators' },
      { label: 'Road Concessionaires', path: '/solutions/road-concessionaires' },
    ],
  },
  company: {
    title: 'Company',
    links: [
      { label: 'About Us', path: '/about' },
      { label: 'Contact', path: '/contact' },
      { label: 'Careers', path: '/contact' },
    ],
  },
  legal: {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Terms of Service', path: '/terms' },
    ],
  },
};

const socialLinks = [
  { icon: LinkedIn, href: 'https://www.linkedin.com/showcase/georoads-ai/', label: 'LinkedIn' },
  { icon: Email, href: 'mailto:info@assetcentrix.co.uk', label: 'Email' },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.dark',
        color: 'white',
        pt: { xs: 8, md: 10 },
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* Logo and Description */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ mb: 3 }}>
              <Box
                component="img"
                src="/geoROAD_v2.png"
                alt="geoROADS.ai"
                sx={{
                  height: 40,
                  filter: 'brightness(0) invert(1)',
                  mb: 2,
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: alpha('#fff', 0.7), maxWidth: 280, lineHeight: 1.8 }}
              >
                AI-powered road infrastructure management platform. Transform your road network with intelligent defect detection and predictive analytics.
              </Typography>
            </Box>

            {/* Social Links */}
            <Stack direction="row" spacing={1}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  sx={{
                    color: alpha('#fff', 0.6),
                    '&:hover': {
                      color: 'white',
                      bgcolor: alpha('#fff', 0.1),
                    },
                  }}
                >
                  <social.icon />
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* Links Columns */}
          {Object.values(footerLinks).map((section) => (
            <Grid size={{ xs: 6, sm: 3, md: 2 }} key={section.title}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'white',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontSize: '0.75rem',
                }}
              >
                {section.title}
              </Typography>
              <Stack spacing={1.5}>
                {section.links.map((link) => (
                  <Typography
                    key={link.label}
                    component={Link}
                    to={link.path}
                    variant="body2"
                    sx={{
                      color: alpha('#fff', 0.6),
                      textDecoration: 'none',
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: 'white',
                      },
                    }}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ borderColor: alpha('#fff', 0.1), mb: 4 }} />

        {/* Bottom Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: alpha('#fff', 0.5) }}
          >
            {currentYear} geoROADS.ai. All rights reserved.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {/* Partner logos placeholder */}
            <Typography
              variant="caption"
              sx={{ color: alpha('#fff', 0.4) }}
            >
              Trusted by leading road authorities
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
